/**
 * ESPN live draft sync — mirrors the Sleeper pipeline: fetch league draft
 * detail, map picks onto the redraft board, poll on an interval.
 *
 * Public ESPN leagues work from the browser with the same headers the rooting
 * dashboard uses. Private leagues need the user logged into ESPN in the same
 * browser session (credentials) or a server-side proxy — there is no OAuth.
 */

import { getEspnProjectionsCached } from './espnProjections.js';
import { canonicalTeam, normalizeNameKey, teamRoughMatch } from './playerMatch.js';

const ESPN_POSITIONS = {
  1: 'QB',
  2: 'RB',
  3: 'WR',
  4: 'TE',
  5: 'K',
  16: 'DST',
};

const ESPN_SLOT_TO_SLEEPER = {
  0: 'QB',
  2: 'RB',
  4: 'WR',
  6: 'TE',
  16: 'DEF',
  17: 'FLEX',
  23: 'SUPER_FLEX',
  20: 'BN',
  21: 'IR',
};

const ESPN_STAT_TO_SLEEPER = {
  3: 'pass_yd',
  4: 'pass_td',
  20: 'pass_int',
  24: 'rush_yd',
  25: 'rush_td',
  42: 'rec_yd',
  43: 'rec_td',
  53: 'rec',
  72: 'fum_lost',
};

const TEAMS_BY_ABBR = {
  ATL: 'ATL', BUF: 'BUF', CHI: 'CHI', CIN: 'CIN', CLE: 'CLE', DAL: 'DAL', DEN: 'DEN', DET: 'DET',
  GB: 'GB', TEN: 'TEN', IND: 'IND', KC: 'KC', LV: 'LV', LAR: 'LAR', MIA: 'MIA', MIN: 'MIN', NE: 'NE',
  NO: 'NO', NYG: 'NYG', NYJ: 'NYJ', PHI: 'PHI', ARI: 'ARI', PIT: 'PIT', LAC: 'LAC', SF: 'SF',
  SEA: 'SEA', TB: 'TB', WAS: 'WAS', CAR: 'CAR', JAX: 'JAX', BAL: 'BAL', HOU: 'HOU',
};

const positionsMatch = (espnPos, boardPos) => {
  if (!espnPos || !boardPos) return false;
  const e = String(espnPos).toUpperCase();
  const b = String(boardPos).toUpperCase();
  if (e === b) return true;
  if ((e === 'K' || e === 'PK') && (b === 'K' || b === 'PK')) return true;
  if ((e === 'DST' || e === 'D/ST' || e === 'DEF') && (b === 'DST' || b === 'DEF')) return true;
  return false;
};

const lastNameKey = (name) => normalizeNameKey(name).split(' ').slice(-1)[0] || '';

const findBoardRowByLastName = (name, position, team, board) => {
  const wantLast = lastNameKey(name);
  if (!wantLast || wantLast.length < 4) return null;

  const candidates = board.filter((row) => {
    if (row.isPick) return false;
    if (!positionsMatch(position, row.position)) return false;
    if (lastNameKey(row.name) !== wantLast) return false;
    return canonicalTeam(team) && canonicalTeam(team) === canonicalTeam(row.team);
  });

  return candidates.length === 1 ? candidates[0] : null;
};

const findBoardRowForEspnPlayer = (player, board) => {
  const fullName =
    player?.fullName || `${player?.firstName ?? ''} ${player?.lastName ?? ''}`.trim();
  const wantName = normalizeNameKey(fullName);
  if (!wantName) return null;

  const wantPos = ESPN_POSITIONS[player?.defaultPositionId] || player?.position || '';
  const wantTeam =
    player?.proTeamAbbreviation ||
    TEAMS_BY_ABBR[player?.proTeamId] ||
    player?.team ||
    'FA';

  const candidates = board.filter((row) => {
    if (row.isPick) return false;
    if (!positionsMatch(wantPos, row.position)) return false;
    if (normalizeNameKey(row.name) !== wantName) return false;
    if (!teamRoughMatch(wantTeam, row.team)) return false;
    return true;
  });

  if (candidates.length === 1) return candidates[0];
  if (candidates.length > 1) {
    const withTeam = candidates.filter((c) => teamRoughMatch(wantTeam, c.team));
    if (withTeam.length === 1) return withTeam[0];
  }

  return findBoardRowByLastName(fullName, wantPos, wantTeam, board);
};

export const espnPositionLabel = (positionId) => ESPN_POSITIONS[positionId] || '—';

export const espnScoringToSleeper = (scoringSettings = {}) => {
  const out = {};
  for (const item of scoringSettings?.scoringItems || []) {
    const key = ESPN_STAT_TO_SLEEPER[item.statId];
    if (key) out[key] = Number(item.points) || 0;
  }
  return out;
};

export const parseEspnRosterPositions = (lineupSlotCounts = {}) => {
  const rosterPositions = [];
  for (const [slotId, count] of Object.entries(lineupSlotCounts)) {
    const slot = ESPN_SLOT_TO_SLEEPER[Number(slotId)];
    if (!slot) continue;
    for (let i = 0; i < Number(count); i += 1) rosterPositions.push(slot);
  }
  return rosterPositions;
};

const buildLeagueUrl = (leagueId, season) => {
  const lid = String(leagueId).trim();
  const yr = Number(season);
  const views =
    'view=mDraftDetail&view=mSettings&view=mTeam&view=mRoster&view=mNav';
  if (yr && yr < new Date().getFullYear()) {
    return `https://fantasy.espn.com/apis/v3/games/ffl/leagueHistory/${lid}?${views}&seasonId=${yr}`;
  }
  return `https://fantasy.espn.com/apis/v3/games/ffl/seasons/${yr || new Date().getFullYear()}/segments/0/leagues/${lid}?${views}`;
};

const fetchJson = async (url, { credentials = 'omit' } = {}) => {
  const response = await fetch(url, {
    credentials,
    headers: {
      Accept: 'application/json',
      'X-Fantasy-Source': 'kona',
    },
  });

  if (!response.ok) {
    throw new Error(
      response.status === 401 || response.status === 403
        ? 'ESPN rejected the request. Use a public league, or log into ESPN in this browser and try again.'
        : `ESPN request failed (${response.status}).`,
    );
  }

  const data = await response.json();
  return Array.isArray(data) ? data[0] : data;
};

export const fetchEspnLeague = async ({ leagueId, season, credentials = 'omit' } = {}) => {
  const lid = leagueId?.trim();
  const yr = String(season || new Date().getFullYear()).trim();
  if (!lid) throw new Error('Enter your ESPN league ID.');
  if (!yr) throw new Error('Enter the ESPN season year (e.g. 2025).');

  return fetchJson(buildLeagueUrl(lid, yr), { credentials });
};

const formatEspnTeamName = (team) =>
  `${team?.location ?? ''} ${team?.nickname ?? ''}`.trim() || `Team ${team?.id}`;

export const parseEspnTeams = (league) =>
  (league?.teams || []).map((team) => ({
    rosterId: String(team.id),
    teamName: formatEspnTeamName(team),
    ownerId: team?.primaryOwner ? String(team.primaryOwner) : '',
    draftOrder: team?.draftOrder ?? null,
  }));

export const parseEspnLeagueMeta = (league) => {
  const settings = league?.settings || {};
  const scoringSettings = settings?.scoringSettings || {};
  const sleeperScoring = espnScoringToSleeper(scoringSettings);
  const rec = Number(sleeperScoring.rec ?? 0);
  const rosterPositions = parseEspnRosterPositions(settings?.rosterSettings?.lineupSlotCounts || {});
  const teamCount = (league?.teams || []).length;
  const rounds =
    Number(settings?.draftSettings?.pickOrder?.length) ||
    Number(settings?.draftSettings?.rounds) ||
    Number(settings?.rosterSettings?.lineupSlotCounts?.[20]) ||
    16;

  return {
    leagueId: String(league?.id || ''),
    ppr: rec,
    scoringSettings: sleeperScoring,
    name: settings?.name || league?.settings?.name || 'ESPN league',
    season: String(league?.seasonId || settings?.seasonId || ''),
    status: league?.status?.currentMatchupPeriod ? 'in_season' : 'pre_draft',
    totalRosters: teamCount,
    rosterPositions,
    scoringLabel: rec >= 1 ? 'PPR' : rec > 0 ? `${rec} PPR` : 'Standard',
  };
};

const extractLeaguePlayers = (league) => {
  const byId = new Map();
  for (const team of league?.teams || []) {
    for (const entry of team?.roster?.entries || []) {
      const player = entry?.playerPoolEntry?.player;
      if (player?.id) byId.set(Number(player.id), player);
    }
  }
  return byId;
};

let espnIdLookupCache = null;

const getEspnIdLookup = async () => {
  if (espnIdLookupCache) return espnIdLookupCache;
  const payload = await getEspnProjectionsCached();
  const byId = new Map();
  for (const row of payload.players || []) {
    if (row.espnId != null) byId.set(Number(row.espnId), row);
  }
  espnIdLookupCache = byId;
  return byId;
};

const resolveEspnPlayer = (playerId, leaguePlayers, projectionLookup) => {
  const id = Number(playerId);
  if (!id) return null;

  const fromLeague = leaguePlayers.get(id);
  if (fromLeague) return fromLeague;

  const fromProjection = projectionLookup.get(id);
  if (fromProjection) {
    return {
      id,
      fullName: fromProjection.name,
      defaultPositionId: Object.entries(ESPN_POSITIONS).find(
        ([, label]) => label === fromProjection.position,
      )?.[0],
      proTeamAbbreviation: fromProjection.team,
      position: fromProjection.position,
      team: fromProjection.team,
    };
  }

  return { id };
};

export const espnDraftStatus = (draftDetail = {}) => {
  if (draftDetail.drafted) return 'complete';
  if (draftDetail.inProgress) return 'drafting';
  return 'pre_draft';
};

export const pollIntervalForEspnStatus = (status) => {
  if (status === 'drafting') return 5000;
  if (status === 'pre_draft') return 12000;
  if (status === 'complete') return 30000;
  return 8000;
};

const buildTeamNameMap = (teams) =>
  new Map((teams || []).map((t) => [String(t.rosterId), t.teamName]));

export const mapEspnPicksToBoardState = async (picks, board, teamNameById = new Map(), league) => {
  const leaguePlayers = extractLeaguePlayers(league);
  const projectionLookup = await getEspnIdLookup();
  const draftedIds = new Set();
  const draftLog = [];
  const unmatched = [];

  const sorted = [...(picks || [])].sort(
    (a, b) => Number(a.overallPickNumber) - Number(b.overallPickNumber),
  );

  for (const pick of sorted) {
    if (!pick?.playerId) continue;

    const player = resolveEspnPlayer(pick.playerId, leaguePlayers, projectionLookup);
    const row = findBoardRowForEspnPlayer(player, board);
    const playerName =
      player?.fullName ||
      `${player?.firstName ?? ''} ${player?.lastName ?? ''}`.trim() ||
      `Player ${pick.playerId}`;
    const playerPos =
      player?.position ||
      espnPositionLabel(player?.defaultPositionId) ||
      '—';
    const teamName = teamNameById.get(String(pick.teamId)) || `Team ${pick.teamId}`;

    if (!row) {
      unmatched.push({
        pickNo: pick.overallPickNumber,
        name: playerName,
        position: playerPos,
        rosterId: pick.teamId,
        teamName,
      });
      continue;
    }

    draftedIds.add(row.id);
    draftLog.push({
      id: row.id,
      name: row.name,
      position: row.position,
      // Carried so the log's avatar has a team logo to fall back on.
      team: row.team,
      value: row.value,
      at: Number(pick.overallPickNumber) * 1000,
      pickNo: pick.overallPickNumber,
      round: pick.roundId,
      rosterId: pick.teamId,
      teamName,
      source: 'espn',
    });
  }

  draftLog.sort((a, b) => Number(b.pickNo) - Number(a.pickNo));

  return {
    draftedIds,
    draftLog,
    unmatched,
    pickCount: sorted.filter((p) => p?.playerId).length,
  };
};

export const connectEspnLeague = async ({ leagueId, season, credentials = 'omit' } = {}) => {
  const league = await fetchEspnLeague({ leagueId, season, credentials });
  const meta = parseEspnLeagueMeta(league);
  const teams = parseEspnTeams(league);
  return { league, meta, teams };
};

export const syncDraftFromEspn = async ({
  leagueId,
  season,
  board,
  teams = [],
  credentials = 'omit',
} = {}) => {
  const league = await fetchEspnLeague({ leagueId, season, credentials });
  const draftDetail = league?.draftDetail || {};
  const picks = draftDetail.picks || [];
  const teamNameById = buildTeamNameMap(teams);
  const mapped = await mapEspnPicksToBoardState(picks, board, teamNameById, league);

  const settings = league?.settings || {};
  const teamCount = teams.length || (league?.teams || []).length;
  const rounds =
    Number(settings?.draftSettings?.pickOrder?.length) ||
    Math.ceil(picks.length / Math.max(teamCount, 1)) ||
    16;
  const status = espnDraftStatus(draftDetail);

  return {
    draft: league,
    ...mapped,
    totalPicks: teamCount && rounds ? teamCount * rounds : null,
    status,
    draftName: settings?.name || 'ESPN draft',
    pollMs: pollIntervalForEspnStatus(status),
  };
};
